/**
 * Copyright-free UI / game SFX — all procedural Web Audio (no sample packs).
 */

export type SfxKind =
  | "hover"
  | "click"
  | "confirm"
  | "transition"
  | "hit"
  | "miss"
  | "combo"
  | "scoreTick"
  | "scoreSlam"
  | "win"
  | "lose"
  | "levelUp"
  | "countdown"
  | "coin";

export class SfxEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private volume = 0.55;
  private lastHover = 0;

  attach(ctx: AudioContext): void {
    if (this.ctx === ctx && this.master) return;
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    this.master.connect(ctx.destination);
  }

  async unlock(ctx: AudioContext): Promise<void> {
    this.attach(ctx);
    if (ctx.state === "suspended") await ctx.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(muted ? 0 : this.volume, this.ctx.currentTime, 0.02);
    }
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master && this.ctx && !this.muted) {
      this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
    }
  }

  play(kind: SfxKind): void {
    if (!this.ctx || !this.master || this.muted) return;
    const t = this.ctx.currentTime;
    switch (kind) {
      case "hover":
        if (t - this.lastHover < 0.045) return;
        this.lastHover = t;
        this.blip(880, 0.04, 0.06, "triangle");
        break;
      case "click":
        this.blip(420, 0.05, 0.12, "square");
        this.noiseBurst(0.03, 0.08, 1800);
        break;
      case "confirm":
        this.chord([523.25, 659.25, 783.99], 0.22, 0.14);
        break;
      case "transition":
        this.whoosh(0.28);
        this.blip(180, 0.12, 0.1, "sawtooth");
        break;
      case "hit":
        this.blip(760, 0.06, 0.11, "triangle");
        break;
      case "miss":
        this.blip(110, 0.14, 0.18, "sawtooth");
        this.noiseBurst(0.08, 0.12, 400);
        break;
      case "combo":
        this.chord([880, 1108.73, 1318.51], 0.16, 0.1);
        break;
      case "scoreTick":
        this.blip(990 + Math.random() * 80, 0.03, 0.07, "square");
        break;
      case "scoreSlam":
        this.noiseBurst(0.05, 0.22, 220);
        this.blip(90, 0.18, 0.28, "sawtooth");
        this.chord([261.63, 329.63, 392], 0.35, 0.2);
        break;
      case "win":
        this.arpeggio([523.25, 659.25, 783.99, 1046.5], 0.09, 0.12);
        setTimeout(() => this.chord([523.25, 659.25, 783.99, 987.77], 0.45, 0.16), 360);
        break;
      case "lose":
        this.arpeggio([392, 349.23, 293.66, 220], 0.11, 0.14);
        this.noiseBurst(0.12, 0.2, 300);
        break;
      case "levelUp":
        this.arpeggio([392, 523.25, 659.25, 784, 1046.5], 0.07, 0.1);
        break;
      case "countdown":
        this.blip(660, 0.08, 0.1, "square");
        break;
      case "coin":
        this.blip(1320, 0.05, 0.09, "triangle");
        this.blip(1760, 0.04, 0.07, "triangle", 0.04);
        break;
    }
  }

  /** Spoken 3-2-1-GO — energetic announcer style + punchy tones. */
  speakCount(n: 3 | 2 | 1 | "go"): void {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
        const phrase = n === "go" ? "Let's go!" : String(n);
        const u = new SpeechSynthesisUtterance(phrase);
        u.rate = n === "go" ? 1.45 : 1.28;
        u.pitch = n === "go" ? 1.35 : 0.95 + (4 - (typeof n === "number" ? n : 1)) * 0.18;
        u.volume = this.muted ? 0 : 1;
        const voices = window.speechSynthesis.getVoices();
        const energetic =
          voices.find((v) => /en(-|_)?(US|GB)/i.test(v.lang) && /male|daniel|google|microsoft/i.test(v.name)) ||
          voices.find((v) => /en/i.test(v.lang));
        if (energetic) u.voice = energetic;
        window.speechSynthesis.speak(u);
      } catch {
        /* ignore */
      }
    }
    if (n === "go") {
      this.noiseBurst(0.06, 0.2, 600);
      this.arpeggio([523.25, 659.25, 783.99, 1046.5], 0.05, 0.18);
      this.chord([523.25, 659.25, 783.99], 0.35, 0.2);
    } else {
      const freqs = { 3: 330, 2: 440, 1: 554 } as const;
      this.blip(freqs[n], 0.22, 0.22, "square");
      this.noiseBurst(0.04, 0.12, 900);
    }
  }

  private tone(
    freq: number,
    dur: number,
    peak: number,
    type: OscillatorType,
    delay = 0,
  ): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + delay;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(Math.max(0.001, peak), t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private blip(
    freq: number,
    dur: number,
    peak: number,
    type: OscillatorType,
    delay = 0,
  ): void {
    this.tone(freq, dur, peak, type, delay);
  }

  private chord(freqs: number[], dur: number, peak: number): void {
    const each = peak / Math.max(1, freqs.length * 0.7);
    for (const f of freqs) this.tone(f, dur, each, "triangle");
  }

  private arpeggio(freqs: number[], step: number, peak: number): void {
    freqs.forEach((f, i) => this.tone(f, step * 1.4, peak, "triangle", i * step));
  }

  private noiseBurst(dur: number, peak: number, cutoff: number): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime;
    const len = Math.max(1, Math.floor(this.ctx.sampleRate * dur));
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = cutoff;
    filter.Q.value = 0.8;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(peak, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    src.start(t0);
    src.stop(t0 + dur + 0.02);
  }

  private whoosh(dur: number): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime;
    const len = Math.max(1, Math.floor(this.ctx.sampleRate * dur));
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(400, t0);
    filter.frequency.exponentialRampToValueAtTime(4200, t0 + dur * 0.55);
    filter.frequency.exponentialRampToValueAtTime(200, t0 + dur);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    src.start(t0);
    src.stop(t0 + dur + 0.02);
  }
}

export const sfx = new SfxEngine();
